<template>
  <div>
    <div class="mb-4 box-title">Change Password</div>

    <div class="mb-4">
      Please enter the 6-digit confirmation code we sent to your email and
      create a new password.
    </div>

    <div
      v-if="isShowError"
      class="alert alert-danger alert-outline alert-dismissible fade show"
      role="alert"
    >
      <i class="uil uil-exclamation-octagon font-size-16 text-danger me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="closeError"></button>
    </div>

    <ValidationObserver v-slot="{ handleSubmit, reset }">
      <b-form
        ref="loginForm"
        @submit.prevent="handleSubmit(requestChangePassword)"
        @reset.prevent="reset"
      >
        <b-form-group class="mb-4">
          <label>Confirmation Code</label>
          <validation-provider
            name="code"
            rules="required"
            v-slot="validationContext"
          >
            <b-form-input
              :state="getValidationState(validationContext)"
              placeholder="Enter 6 Digit Confirmation Code"
              v-model="code"
              maxlength="6"
              trim
            ></b-form-input>
            <b-form-invalid-feedback>
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>
        </b-form-group>

        <b-form-group class="mb-4">
          <label>New Password</label>
          <validation-provider
            name="password"
            rules="required|verify_password"
            v-slot="validationContext"
          >
            <b-input-group>
              <template #append>
                <b-input-group-text
                  class="toggle-password"
                  :class="{
                    'input-text-red':
                      getValidationState(validationContext) === false,
                    'input-text-green':
                      getValidationState(validationContext) === true,
                  }"
                  @click="() => (isShowPassword = !isShowPassword)"
                  >&nbsp;<i
                    class="far"
                    :class="{
                      'fa-eye': isShowPassword,
                      'fa-eye-slash': !isShowPassword,
                    }"
                  ></i
                ></b-input-group-text>
              </template>
              <b-form-input
                class="mb-2 no-border-right"
                :state="getValidationState(validationContext)"
                placeholder="Enter New Password"
                name="password"
                @input="handleInput"
                v-model="password"
                :type="isShowPassword ? 'text' : 'password'"
                trim
              ></b-form-input>
            </b-input-group>

            <div class="password-verification">
              <div class="mb-1">Password must include:</div>
              <b-row>
                <b-col :class="{ green: validationData.oneCapital }" cols="4"
                  ><i
                    :class="{ grey: !validationData.oneCapital }"
                    class="fas fa-check-circle"
                  ></i>
                  Atleast 1 capital letter</b-col
                >
                <b-col :class="{ green: validationData.oneNumber }" cols="4"
                  ><i
                    :class="{ grey: !validationData.oneNumber }"
                    class="fas fa-check-circle"
                  ></i>
                  Atleast 1 number</b-col
                >
                <b-col cols="4"></b-col>
                <b-col :class="{ green: validationData.oneLower }" cols="4"
                  ><i
                    :class="{ grey: !validationData.oneLower }"
                    class="fas fa-check-circle"
                  ></i>
                  Atleast 1 lower letter</b-col
                >
                <b-col :class="{ green: validationData.oneSymbol }" cols="5"
                  ><i
                    :class="{ grey: !validationData.oneSymbol }"
                    class="fas fa-check-circle"
                  ></i>
                  Atleast 1 symbol (ex: ?@#)</b-col
                >
                <b-col cols="3"></b-col>
                <b-col
                  :class="{ green: validationData.eightCharacter }"
                  cols="4"
                  ><i
                    :class="{ grey: !validationData.eightCharacter }"
                    class="fas fa-check-circle"
                  ></i>
                  Atleast 8 characters</b-col
                >
                <b-col cols="4"></b-col>
                <b-col cols="4"></b-col>
              </b-row>
            </div>
          </validation-provider>
        </b-form-group>

        <b-form-group class="mb-4">
          <label>Confirm New Password</label>
          <validation-provider
            name="confirm password"
            rules="required|confirmed:password"
            v-slot="validationContext"
          >
            <b-input-group>
              <template #append>
                <b-input-group-text
                  class="toggle-password"
                  :class="{
                    'input-text-red':
                      getValidationState(validationContext) === false,
                    'input-text-green':
                      getValidationState(validationContext) === true,
                  }"
                  @click="
                    () => (isShowConfirmPassword = !isShowConfirmPassword)
                  "
                  >&nbsp;<i
                    class="far"
                    :class="{
                      'fa-eye': isShowConfirmPassword,
                      'fa-eye-slash': !isShowConfirmPassword,
                    }"
                  ></i
                ></b-input-group-text>
              </template>
              <b-form-input
                class="no-border-right"
                :state="getValidationState(validationContext)"
                placeholder="Confirm New Password"
                v-model="confirmPassword"
                :type="isShowConfirmPassword ? 'text' : 'password'"
                trim
              ></b-form-input>
            </b-input-group>
            <b-form-invalid-feedback>
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>
        </b-form-group>

        <b-button
          class="w-100"
          variant="primary"
          type="submit"
          :disabled="isRequestLoading"
        >
          <span
            v-if="isRequestLoading"
            class="spinner-grow spinner-grow-sm"
            role="status"
            aria-hidden="true"
          ></span>
          Change Password
        </b-button>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script>
import { authMethods } from "@/state/helpers";

export default {
  props: ["username", "value"],
  data() {
    return {
      isShowPassword: false,
      isShowConfirmPassword: false,
      isShowError: false,
      errorMessage: null,
      isRequestLoading: false,
      code: null,
      password: this.value,
      confirmPassword: null,
    };
  },
  computed: {
    validationData() {
      var validationData = {
        oneCapital: this.password && this.password.match(/[A-Z]/),
        oneNumber: this.password && this.password.match(/[0-9]/),
        oneLower: this.password && this.password.match(/[a-z]/),
        oneSymbol:
          this.password &&
          //eslint-disable-next-line
          this.password.match(/[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/),
        eightCharacter: this.password && this.password.length >= 8,
      };

      return validationData;
    },
  },
  methods: {
    ...authMethods,
    handleInput() {
      this.$emit("input", this.password);
    },
    closeError() {
      this.isShowError = false;
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },
    requestChangePassword() {
      this.isRequestLoading = true;
      this.isShowError = false;
      this.errorMessage = null;
      this.resetPassword({
        username: this.username,
        code: this.code,
        newPassword: this.password,
      })
        .then(() => {
          this.isRequestLoading = false;
          this.$emit("success");
        })
        .catch((err) => {
          this.isRequestLoading = false;
          this.errorMessage = err.message;
          this.isShowError = true;
        });
    },
  },
};
</script>

<style>
.password-verification {
  font-size: 11px;
  font-weight: 500;
}

.no-border-right {
  border-right: none;
}

.input-text-green {
  border-color: #51d28c;
}

.input-text-red {
  border-color: #f34e4e;
}

.toggle-password {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  cursor: pointer;
  background: none;
  color: #e1e1e1;
}
</style>